import { makeAutoObservable, runInAction } from 'mobx';
import { toast } from 'react-toastify';
import { statusCodesScreenServices } from 'services/ReferenceScreenServices/statusCodesServices';
import {
  StatusCode,
} from 'Interfaces/ReferenceInterfaces/StatusCodesInterface';
import { StatusTransition } from 'Interfaces/JobsInterface';
import { JobPasteServices } from 'services/jobPasteServices';

export class StatusCodesStore {
  statusCodes: StatusCode[] = [];
  isLoading: boolean = false;
  pagination = {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    hasNext: false,
    hasPrevious: false
  };
  selectedStatusCode: StatusCode | null = null;

  possibleTransitions: Record<string, StatusTransition[]> = {};
  possibleTransitionsLoading: boolean = false;
  currentStatusTransitions: StatusTransition[] = []; // For the currently selected status

  constructor() {
    makeAutoObservable(this);
  }

  fetchStatusCodes = async (page = 1, pageSize = 100) => {
    this.isLoading = true;
    try {
      const response = await statusCodesScreenServices.getAllStatusCodes(page, pageSize);
      runInAction(() => {
        this.statusCodes = response.data.sort((a, b) => a.description?.localeCompare(b.description || '') || 0);
        this.pagination = {
          currentPage: response.pagination.current_page,
          totalPages: response.pagination.total_pages,
          totalItems: response.pagination.total_items,
          hasNext: response.pagination.has_next,
          hasPrevious: response.pagination.has_previous
        };
      });
    } catch (error) {
      toast.error('Error fetching status codes');
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  fetchPossibleStatusTransitions = async (department: string) => {
    this.possibleTransitionsLoading = true;
    try {
      const response = await JobPasteServices.getPossibleStatusTransitions(department);
      runInAction(() => {
        this.possibleTransitions = response;
      });
    } catch (error) {
      toast.error('Error fetching possible status transitions');
    } finally {
      runInAction(() => {
        this.possibleTransitionsLoading = false;
      });
    }
  };

  getTransitionsForStatus = (status: string): StatusTransition[] => {
    if (!status || !this.possibleTransitions[status.toLowerCase()]) {
      return [];
    }
    return this.possibleTransitions[status.toLowerCase()] || [];
  };

  // Method to set current status transitions
  setCurrentStatusTransitions = (status: string) => {
    this.currentStatusTransitions = this.getTransitionsForStatus(status);
  };

  createStatusCode = async (statusCodeData: Partial<StatusCode>) => {
    this.isLoading = true;
    try {
      const response = await statusCodesScreenServices.createStatusCode(statusCodeData);
      if (response.status) {
        toast.success('Status code created successfully');
        this.fetchStatusCodes(); // Refresh the list
      } else {
        toast.error(response.message || 'Failed to create status code');
      }
    } catch (error) {
      toast.error('Error creating status code');
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  updateStatusCode = async (id: number, statusCodeData: Partial<StatusCode>) => {
    this.isLoading = true;
    try {
      const response = await statusCodesScreenServices.updateStatusCode(id, statusCodeData);
      if (response.status) {
        toast.success('Status code updated successfully');
        this.fetchStatusCodes(); // Refresh the list
      } else {
        toast.error(response.message || 'Failed to update status code');
      }
    } catch (error) {
      toast.error('Error updating status code');
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  deleteStatusCode = async (id: number) => {
    this.isLoading = true;
    try {
      const response = await statusCodesScreenServices.deleteStatusCode(id);
      if (response.status) {
        toast.success('Status code deleted successfully');
        this.fetchStatusCodes(); // Refresh the list
      } else {
        toast.error(response.message || 'Failed to delete status code');
      }
    } catch (error) {
      toast.error('Error deleting status code');
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  setSelectedStatusCode = (statusCode: StatusCode | null) => {
    this.selectedStatusCode = statusCode;
  };
}

export const statusCodesStore = new StatusCodesStore();
